import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';

// PDF Styles optimized for single-page professional layout
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    paddingTop: 20,
    paddingBottom: 40,
    paddingLeft: 20,
    paddingRight: 20,
    backgroundColor: '#ffffff',
    fontSize: 9,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: '#054597',
    paddingBottom: 8,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
    marginTop: 5,
  },
  logo: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  logoDom: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#054597',
    letterSpacing: -0.5,
  },
  logoElec: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#4bae32',
    letterSpacing: -0.5,
  },
  companyDetails: {
    fontSize: 8,
    color: '#333333',
    lineHeight: 1.2,
  },
  companyName: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 2,
  },
  title: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 8,
    marginTop: 5,
    color: '#054597',
    letterSpacing: 0.5,
  },
  section: {
    marginBottom: 6,
  },
  customerSection: {
    backgroundColor: '#ffffff',
    padding: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderStyle: 'solid',
  },
  customerGreeting: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#000000',
  },
  sectionTitle: {
    fontSize: 10, // Reduced from 11
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#054597',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  label: {
    fontSize: 9,
    fontWeight: 'bold',
    width: 120,
  },
  value: {
    fontSize: 9,
    flex: 1,
  },
  componentSection: {
    marginTop: 6,
    marginBottom: 6,
  },
  componentTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#000000',
  },
  componentSubtitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginTop: 5,
    marginBottom: 3,
    color: '#333333',
  },
  checklistItem: {
    flexDirection: 'row',
    marginBottom: 2,
    marginLeft: 8,
  },
  checkbox: {
    width: 10,
    marginRight: 6,
    fontSize: 8,
  },
  checklistText: {
    fontSize: 8,
    flex: 1,
  },
  documentTable: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: '#cccccc',
    borderStyle: 'solid',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    padding: 4,
  },
  tableHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
    flex: 3,
    color: '#000000',
  },
  tableHeaderTick: {
    fontSize: 8,
    fontWeight: 'bold',
    width: 25,
    textAlign: 'center',
    color: '#000000',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    borderBottomStyle: 'solid',
    padding: 3,
    minHeight: 18,
  },
  tableCell: {
    fontSize: 8,
    flex: 3,
  },
  tableTick: {
    fontSize: 8,
    width: 25,
    textAlign: 'center',
  },
  signatureSection: {
    marginTop: 6,
    marginBottom: 6,
  },
  signatureText: {
    fontSize: 8,
    marginBottom: 4,
    lineHeight: 1.2,
  },
  customerComments: {
    marginTop: 5,
    padding: 5,
    borderWidth: 1,
    borderColor: '#cccccc',
    borderStyle: 'solid',
    minHeight: 25,
    backgroundColor: '#ffffff',
  },
  commentsLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 3,
    color: '#000000',
  },
  commentsText: {
    fontSize: 8,
    lineHeight: 1.2,
  },
  signatureFields: {
    marginTop: 6,
  },
  signatureRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  signatureLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    width: 70,
  },
  signatureValue: {
    fontSize: 9,
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    paddingBottom: 1,
  },
  signatureImage: {
    width: 100,
    height: 30,
    marginVertical: 2,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 20,
    right: 20,
  },
  footerText: {
    fontSize: 8,
    textAlign: 'center',
    color: '#666666',
    marginBottom: 3,
  },
  complianceText: {
    fontSize: 6,
    color: '#999999',
    textAlign: 'center',
    lineHeight: 1.1,
    paddingTop: 3,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    borderTopStyle: 'solid',
  },
  separator: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    marginVertical: 10,
  },
});

interface CompletionPDFProps {
  record: any;
  signatureImage?: string;
}

export const CompletionPDFDocument: React.FC<CompletionPDFProps> = ({ record, signatureImage }) => {
  const currentDate = format(new Date(), 'dd/MM/yyyy');
  const completionDate = record.signedAt
    ? format(new Date(record.signedAt), 'dd/MM/yyyy')
    : currentDate;

  // No need to extract system details anymore - all fields are consolidated

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with Logo and Company Details */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {/* Professional logo with correct colors */}
            <View style={styles.logo}>
              <Text style={styles.logoDom}>Dom</Text>
              <Text style={styles.logoElec}>Elec</Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.companyName}>DomElec Electrical & Solar Ltd</Text>
            <Text style={styles.companyDetails}>Suite 9, Llay Business Centre</Text>
            <Text style={styles.companyDetails}>Rackery Lane, Wrexham, LL12 0PB</Text>
          </View>
        </View>

        {/* Title */}
        <Text style={styles.title}>HIES COMPLETION OF WORKS RECORD</Text>

        {/* Customer Information - Professional Layout */}
        <View style={styles.customerSection}>
          <Text style={styles.customerGreeting}>Dear {record.customerName},</Text>

          <View style={styles.row}>
            <Text style={styles.label}>Customer Address:</Text>
            <Text style={styles.value}>{record.customerAddress}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Project Ref No:</Text>
            <Text style={styles.value}>{record.projectRefNumber}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Installation Address:</Text>
            <Text style={styles.value}>{record.installationAddress}</Text>
          </View>
        </View>

        {/* Description of Installation */}
        <View style={styles.componentSection}>
          <Text style={styles.componentTitle}>
            Description of Installation (key components installed):
          </Text>

          <Text style={styles.componentSubtitle}>SOLAR PV SYSTEM:</Text>
          {record.panelDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Solar Panels: {record.panelDetails}</Text>
            </View>
          )}

          {/* Inverters */}
          {record.inverter1Details && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Inverter: {record.inverter1Details}</Text>
            </View>
          )}

          {record.inverter2Details && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Inverter 2: {record.inverter2Details}</Text>
            </View>
          )}

          {record.inverter3Details && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Inverter 3: {record.inverter3Details}</Text>
            </View>
          )}

          {/* Battery */}
          {record.batteryDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Battery Storage: {record.batteryDetails}</Text>
            </View>
          )}

          <Text style={styles.componentSubtitle}>ADDITIONAL COMPONENTS:</Text>

          {record.optimiserDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Optimisers: {record.optimiserDetails}</Text>
            </View>
          )}

          {record.evChargerDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>EV Charger: {record.evChargerDetails}</Text>
            </View>
          )}

          {record.hotWaterDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Hot Water Circuit: {record.hotWaterDetails}</Text>
            </View>
          )}

          {record.atsEpsDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>ATS/EPS: {record.atsEpsDetails}</Text>
            </View>
          )}

          {record.birdGuardDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Bird Guard: {record.birdGuardDetails}</Text>
            </View>
          )}

          {record.monitoringDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>
                Monitoring System: {record.monitoringDetails}
              </Text>
            </View>
          )}

          {record.otherDetails && (
            <View style={styles.checklistItem}>
              <Text style={styles.checkbox}>☐</Text>
              <Text style={styles.checklistText}>Other: {record.otherDetails}</Text>
            </View>
          )}
        </View>

        {/* Document provision text */}
        <View style={styles.section}>
          <Text style={{ fontSize: 8, marginBottom: 5, fontStyle: 'italic', color: '#666' }}>
            We will provide the following documents within 14 days of final balance payment:
          </Text>
        </View>

        {/* Document Checklist Table */}
        <View style={styles.documentTable}>
          <View style={styles.tableHeader}>
            <Text style={styles.tableHeaderText}>Document Checklist</Text>
            <Text style={styles.tableHeaderTick}>✓</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>
              Building Regulations Certificate - delivery method confirmed
            </Text>
            <Text style={styles.tableTick}>{record.confirmationBuildingRegs ? '✓' : '☐'}</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Manufacturer's Installation and Operating Manuals</Text>
            <Text style={styles.tableTick}>{record.manufacturersManuals ? '✓' : '☐'}</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Service Requirements</Text>
            <Text style={styles.tableTick}>{record.serviceRequirements ? '✓' : '☐'}</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Maintenance Requirements</Text>
            <Text style={styles.tableTick}>{record.maintenanceRequirements ? '✓' : '☐'}</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Performance and Financial Estimates</Text>
            <Text style={styles.tableTick}>{record.performanceEstimates ? '✓' : '☐'}</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>MCS or Flexi-Orb Certificate</Text>
            <Text style={styles.tableTick}>{record.mcsCertificate ? '✓' : '☐'}</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>MCS Compliance Certificate</Text>
            <Text style={styles.tableTick}>{record.mcsComplianceConfirmation ? '✓' : '☐'}</Text>
          </View>
        </View>

        {/* Signature Section */}
        <View style={styles.signatureSection}>
          <Text style={styles.signatureText}>
            Please sign and return to confirm you are happy with the installation.
          </Text>
          <Text style={styles.signatureText}>
            I hereby confirm that the installation is complete and has met my requirements. I
            understand that all documents listed above will be provided within 14 days of final
            balance payment.
          </Text>

          {/* Customer Comments Box */}
          <View style={styles.customerComments}>
            <Text style={styles.commentsLabel}>Customer Comments:</Text>
            {record.customerComments && (
              <Text style={styles.commentsText}>{record.customerComments}</Text>
            )}
          </View>

          {/* Signature Fields */}
          <View style={styles.signatureFields}>
            <View style={styles.signatureRow}>
              <Text style={styles.signatureLabel}>Name:</Text>
              <Text style={styles.signatureValue}>
                {record.signedByName || record.customerName}
              </Text>
            </View>

            <View style={styles.signatureRow}>
              <Text style={styles.signatureLabel}>Signature:</Text>
              <View style={styles.signatureValue}>
                {(signatureImage || record.signatureImage) && (
                  <Image
                    style={styles.signatureImage}
                    src={signatureImage || record.signatureImage}
                  />
                )}
              </View>
            </View>

            <View style={styles.signatureRow}>
              <Text style={styles.signatureLabel}>Date:</Text>
              <Text style={styles.signatureValue}>{completionDate}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            DomElec Electrical & Solar Ltd, Company No. 10805156, Registered in England & Wales
          </Text>
          <Text style={styles.complianceText}>
            Compliance Advice Guide © HIES 2022 | Completion of Works Record | Version 1 | Date of
            circulation: 17th October 2022. This guide must only be used by HIES members as elements
            of it relate to the requirements of the HIES Code of Practice. Whilst every effort has
            been made to ensure that the guide is legally correct, only the courts can definitively
            interpret legislation and HIES cannot accept any liability for any legal flaws in the
            guide.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Helper function to generate PDF blob
export const generateCompletionPDF = async (
  record: any,
  signatureImage?: string
): Promise<Blob> => {
  const doc = <CompletionPDFDocument record={record} signatureImage={signatureImage} />;
  const blob = await pdf(doc).toBlob();
  return blob;
};

// Helper function to download PDF
export const downloadCompletionPDF = async (record: any, signatureImage?: string) => {
  try {
    const blob = await generateCompletionPDF(record, signatureImage);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `HIES_Completion_${record.projectRefNumber}_${format(new Date(), 'yyyyMMdd')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// Helper function to preview PDF in new tab
export const previewCompletionPDF = async (record: any, signatureImage?: string) => {
  try {
    const blob = await generateCompletionPDF(record, signatureImage);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    // Clean up after a delay
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (error) {
    console.error('Error generating PDF preview:', error);
    throw error;
  }
};

// Helper function to print PDF directly
export const printCompletionPDF = async (record: any, signatureImage?: string) => {
  try {
    const blob = await generateCompletionPDF(record, signatureImage);
    const url = URL.createObjectURL(blob);

    // Create an iframe to print from
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);

    // Wait for iframe to load then print
    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Clean up after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    };
  } catch (error) {
    console.error('Error printing PDF:', error);
    throw error;
  }
};
